import React, { useContext, useRef, useState } from "react"
import { Dialog } from "@reach/dialog"
import "@reach/dialog/styles.css"
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js"
import { loadStripe, Stripe } from "@stripe/stripe-js"
import { UserContext } from "../../HomeApp"
import CardSection from "../CardSection"
import { SubmitButton } from "../../shared/Button"
import { guestDonationCheckout } from "../../../requests/tips"
import { updateGuestProfile } from "../../../requests/ticketPurchases"
import { X } from "react-bootstrap-icons"
import { useDispatch } from "react-redux"
import { openAuthDialog } from "../../Authentication"

const DONATION_MIN_AMOUNT = 5
const DONATION_MAX_AMOUNT = 100
const SUGGESTED_DONATION_AMOUNTS = [5, 10, 20]

interface GuestDonationCheckoutModalProps {
  postId: number
  bandId: number
  postTitle: string
  donationText?: string | null
  onClose: () => void
}

interface GuestDonationFormProps {
  postId: number
  bandId: number
  postTitle: string
  donationText?: string | null
  onClose: () => void
}

const GuestDonationForm: React.FC<GuestDonationFormProps> = ({
  postId,
  bandId,
  postTitle,
  donationText,
  onClose,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const [email, setEmail] = useState("")
  const [amount, setAmount] = useState<number | "">(DONATION_MIN_AMOUNT)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [emailRegistered, setEmailRegistered] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [donatedAmount, setDonatedAmount] = useState(0)
  const [userToken, setUserToken] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileSaved, setProfileSaved] = useState(false)
  const [profileError, setProfileError] = useState("")
  const [saveCard, setSaveCard] = useState(true)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setIsSubmitting(true)
    setError("")

    try {
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        setError("Card form is not ready. Please try again.")
        return
      }

      const { paymentMethod, error: pmError } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: { email },
        })

      if (pmError) {
        setError(pmError.message || "Could not process card. Please try again.")
        return
      }

      const result = await guestDonationCheckout({
        postId,
        bandId,
        email,
        paymentMethodId: paymentMethod!.id,
        amount: amount === "" ? DONATION_MIN_AMOUNT : amount,
        redirectUrl: window.location.href,
        saveCard,
      })

      if (result.success && result.tip) {
        setDonatedAmount(result.tip.amount)
        if (result.user_token) setUserToken(result.user_token)
        setCompleted(true)
      } else if (result.error_code === "email_registered") {
        setEmailRegistered(true)
      } else {
        const message =
          result.error === "payment_failed"
            ? "Your card was declined. Please check your card details or try a different card."
            : result.error || "Payment failed. Please try again."
        setError(message)
      }
    } catch {
      setError("Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!userToken || (!firstName && !lastName)) return
    setProfileSaving(true)
    setProfileError("")
    const result = await updateGuestProfile({
      token: userToken,
      firstName,
      lastName,
      redirectUrl: window.location.href,
    })
    setProfileSaving(false)
    if (result.success) {
      setProfileSaved(true)
    } else {
      setProfileError(
        result.errors?.join(", ") || "Could not save. Please try again.",
      )
    }
  }

  if (completed) {
    return (
      <div className="pt-2">
        <div className="text-green-700 font-bold text-lg mb-2">
          Thank you for your donation!
        </div>
        <div className="text-sm text-gray-700 mb-3">
          Your ${donatedAmount} donation to <strong>{postTitle}</strong> is
          confirmed. We sent you a receipt to <strong>{email}</strong>.
        </div>

        {userToken && (
          <div className="mt-5 pt-4 border-t border-gray-200">
            {profileSaved ? (
              <div className="text-sm text-gray-700">
                <div className="font-semibold text-green-700 mb-1">
                  Profile saved!
                </div>
                A login link has been sent to <strong>{email}</strong>. Use it
                to access your bandNada account.
              </div>
            ) : (
              <>
                <div className="font-semibold text-gray-800">
                  Finish bandNada Signup
                </div>
                <div className="text-sm text-gray-700 mb-3">
                  bandNada is the network to hear and connect with local
                  independent music. 100% Community Run, 100% Community Owned -{" "}
                  <a
                    href="/own-bandnada"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    including you!
                  </a>
                </div>
                <form onSubmit={handleProfileSave}>
                  <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                    />
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                      className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  {profileError && (
                    <div className="text-red-500 text-xs mb-2">
                      {profileError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={profileSaving || (!firstName && !lastName)}
                    className="px-4 py-2 bg-gray-800 text-white rounded text-sm font-semibold focus:outline-none disabled:opacity-50"
                  >
                    {profileSaving ? "Saving..." : "Finish Signup"}
                  </button>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  if (emailRegistered) {
    return (
      <div className="pt-2">
        <div className="text-gray-800 mb-4">
          An account with <strong>{email}</strong> already exists. Please log in
          to donate.
        </div>
        <button
          className="px-4 py-2 bg-green-700 text-white rounded text-sm font-semibold focus:outline-none"
          onClick={() => {
            onClose()
            dispatch(openAuthDialog({ modalState: "auth" }))
          }}
        >
          Log In
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      {donationText && (
        <p className="text-sm text-gray-700 mb-4">{donationText}</p>
      )}

      <div className="mb-3">
        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
          Suggested Donation
        </div>
        <div className="flex gap-2">
          {SUGGESTED_DONATION_AMOUNTS.map((suggested) => (
            <button
              key={suggested}
              type="button"
              onClick={() => setAmount(suggested)}
              className={`px-3 py-1 rounded border-2 font-bold text-sm focus:outline-none ${
                amount === suggested
                  ? "bg-green-700 border-green-700 text-white"
                  : "bg-transparent border-green-700 text-green-700"
              }`}
            >
              ${suggested}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-1">
          <span className="text-gray-700">$</span>
          <input
            type="number"
            min={DONATION_MIN_AMOUNT}
            max={DONATION_MAX_AMOUNT}
            step="1"
            value={amount}
            onChange={(e) => {
              const raw = e.target.value
              setAmount(raw === "" ? "" : parseInt(raw, 10))
            }}
            className="border border-gray-300 rounded px-2 py-2 w-24 text-sm"
          />
        </div>
        <div className="text-xs text-gray-500 mt-1">$5 - $100</div>
        {amount !== "" && amount < DONATION_MIN_AMOUNT && (
          <div className="text-xs text-red-600 mt-1">
            Please enter an amount of at least ${DONATION_MIN_AMOUNT}.
          </div>
        )}
        {amount !== "" && amount > DONATION_MAX_AMOUNT && (
          <div className="text-xs text-red-600 mt-1">
            Please enter an amount of at most ${DONATION_MAX_AMOUNT}.
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="text-xs uppercase font-bold text-gray-700 mb-1">
          Email
        </div>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="your@email.com"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
        />
        <div className="text-xs text-gray-500 mt-1">
          Your donation receipt will be sent here.
        </div>
      </div>

      <CardSection />

      <label className="flex items-center gap-2 text-sm text-gray-700 mt-3 mb-1 cursor-pointer">
        <input
          type="checkbox"
          checked={saveCard}
          onChange={(e) => setSaveCard(e.target.checked)}
        />
        Save card for future purchases
      </label>

      {error && <div className="text-red-500 text-sm mb-3">{error}</div>}

      <SubmitButton
        bg="bg-green-700"
        type="submit"
        active={
          !isSubmitting &&
          !!stripe &&
          amount !== "" &&
          amount >= DONATION_MIN_AMOUNT &&
          amount <= DONATION_MAX_AMOUNT
        }
        className=""
      >
        {isSubmitting
          ? "Processing..."
          : `Donate $${amount === "" ? DONATION_MIN_AMOUNT : amount}`}
      </SubmitButton>

      <div className="text-xs text-gray-500 mt-3">
        I authorize bandNada to charge my card for this donation.
      </div>

      <div className="text-sm text-gray-500 mt-4 pt-4 border-t border-gray-200">
        Already have a bandNada account?{" "}
        <button
          type="button"
          className="text-blue-600 hover:text-blue-800 focus:outline-none"
          onClick={() => {
            onClose()
            dispatch(openAuthDialog({ modalState: "auth" }))
          }}
        >
          Log in
        </button>
      </div>
    </form>
  )
}

const GuestDonationCheckoutModal: React.FC<GuestDonationCheckoutModalProps> = ({
  postId,
  bandId,
  postTitle,
  donationText,
  onClose,
}) => {
  const { spk } = useContext(UserContext)
  const stripePromiseRef = useRef<Promise<Stripe | null>>()
  if (!stripePromiseRef.current) {
    stripePromiseRef.current = loadStripe(spk)
  }

  return (
    <Dialog
      onDismiss={onClose}
      aria-label="Donate to the show"
      className="relative"
    >
      <div className="flex justify-between items-start mb-4">
        <div></div>
        <button
          onClick={onClose}
          className="ml-4 text-gray-500 hover:text-gray-800 focus:outline-none"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <h1 className="border-b-4 border-black mb-6 pb-1 text-xl">
        Donate to <span className="font-bold">{postTitle}</span>
      </h1>

      <Elements stripe={stripePromiseRef.current}>
        <GuestDonationForm
          postId={postId}
          bandId={bandId}
          postTitle={postTitle}
          donationText={donationText}
          onClose={onClose}
        />
      </Elements>
    </Dialog>
  )
}

export default GuestDonationCheckoutModal
